/**
 * useReplyCadenceBatch — fetches per-reply cadence metrics (Speed to Lead,
 * Follow-Ups, Between FUs, Reply → Book) for the visible page of rows in
 * the Master Inbox table. Backed by the get_reply_cadence_batch RPC, which
 * computes everything from masterinbox_replies.api_response_get_messages
 * + masterinbox_label_updates in a single round-trip.
 *
 * Returns null for rows that aren't human + positive — those rows render
 * as em-dash in the table, since the math is only meaningful there.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface ReplyCadenceMetrics {
  speed_to_lead_seconds: number | null
  followups_count: number
  between_fus_seconds: number | null
  reply_to_book_seconds: number | null
}

export type ReplyCadenceMap = Map<string, ReplyCadenceMetrics>

export function useReplyCadenceBatch(replyIds: string[]) {
  // Stable cache key — sort the IDs so identical sets dedupe across renders
  const sortedIds = [...replyIds].sort()
  const cacheKey = sortedIds.join(',')

  return useQuery<ReplyCadenceMap>({
    queryKey: ['reply-cadence-batch', cacheKey],
    enabled: replyIds.length > 0,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_reply_cadence_batch', {
        p_reply_ids: sortedIds,
      })
      if (error) {
        console.error('[useReplyCadenceBatch] RPC error', error)
        return new Map()
      }
      const map: ReplyCadenceMap = new Map()
      for (const row of (data ?? []) as any[]) {
        map.set(row.reply_id, {
          speed_to_lead_seconds:
            row.speed_to_lead_seconds == null ? null : Number(row.speed_to_lead_seconds),
          followups_count: Number(row.followups_count ?? 0),
          between_fus_seconds:
            row.between_fus_seconds == null ? null : Number(row.between_fus_seconds),
          reply_to_book_seconds:
            row.reply_to_book_seconds == null ? null : Number(row.reply_to_book_seconds),
        })
      }
      return map
    },
  })
}

export function formatCadenceDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—'
  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes < 1) return '<1m'
  if (totalMinutes < 60) return `${totalMinutes}m`
  const hours = seconds / 3600
  if (hours < 48) {
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)
    return m === 0 ? `${h}h` : `${h}h ${m}m`
  }
  const days = hours / 24
  return `${days.toFixed(1)}d`
}
